import { useState, useEffect, useCallback } from "react";
import { Search, Trash2 } from "lucide-react";
import styles from "../../../styles/__labTechnicianManagement.module.scss";
import dialogStyles from "../../../styles/__labDialogAddEditTest.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { IoAddCircleOutline } from "react-icons/io5";
import LabDialogAddEditTechnical from "../../../components/lab-dialog-AddEditTechnical/LabDialogAddEditTechnical";
import LabDialogViewTechnical from "../../../components/lab-dialog-ViewTechnical/LabDialogViewTechnical";
import {
  fetchImagingTechnicians,
  deleteImagingTechnician,
} from "../../../slice/imagingTechnicians/imagingTechniciansSlice";

const TechnicianManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;

  const [openDialogAddEditTechnical, setOpenDialogAddEditTechnical] =
    useState(false);
  const [openDialogViewTechnical, setOpenDialogViewTechnical] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [dialogType, setDialogType] = useState("add");
  // Delete-technician confirmation modal state
  const [technicianToDelete, setTechnicianToDelete] = useState(null);
  const [deletingTechnician, setDeletingTechnician] = useState(false);

  const { technicians, pagination, isLoading, error } = useSelector(
    (state) => state.imagingTechnicians,
  );

  const totalPages = pagination?.totalPages || 1;

  const fetchTechnicians = useCallback(
    (page = 1, status = activeTab, search = searchTerm) => {
      const params = { page, limit: itemsPerPage };
      if (search && search.trim()) params.search = search.trim();
      // Delete is a soft delete on the backend (status -> INACTIVE) and the
      // Active/Inactive tabs and status toggle are hidden, so the "All" list
      // shows ACTIVE technicians only; otherwise deleted ones stay visible.
      params.status =
        !status || status === "All" ? "ACTIVE" : status.toUpperCase();
      dispatch(fetchImagingTechnicians(params));
    },
    [dispatch, activeTab, searchTerm, itemsPerPage],
  );

  useEffect(() => {
    fetchTechnicians(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    fetchTechnicians(1, tab, searchTerm);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    fetchTechnicians(page);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchTechnicians(1, activeTab, searchTerm);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setActiveTab("All");
    setCurrentPage(1);
    fetchTechnicians(1, "All", "");
  };

  const handleAddNew = () => {
    setSelectedTechnician(null);
    setDialogType("add");
    setOpenDialogAddEditTechnical(true);
  };

  const handleEdit = (tech) => {
    setSelectedTechnician(tech);
    setDialogType("edit");
    setOpenDialogAddEditTechnical(true);
  };

  const handleView = (tech) => {
    setSelectedTechnician(tech);
    setOpenDialogViewTechnical(true);
  };

  // Open the confirmation modal instead of the native confirm dialog.
  const handleDelete = (tech) => {
    setTechnicianToDelete(tech);
  };

  const closeDeleteModal = () => {
    if (deletingTechnician) return;
    setTechnicianToDelete(null);
  };

  const confirmDeleteTechnician = async () => {
    if (!technicianToDelete?.id) return;
    setDeletingTechnician(true);
    try {
      await dispatch(deleteImagingTechnician(technicianToDelete.id)).unwrap();
      setTechnicianToDelete(null);
      // If the last row on a page was removed, step back a page.
      const page =
        technicians.length === 1 && currentPage > 1
          ? currentPage - 1
          : currentPage;
      setCurrentPage(page);
      fetchTechnicians(page);
    } catch {
      // Error toast is shown by the thunk; keep the modal open.
    } finally {
      setDeletingTechnician(false);
    }
  };

  const handleDialogClose = (refreshList = false) => {
    setOpenDialogAddEditTechnical(false);
    setOpenDialogViewTechnical(false);
    setSelectedTechnician(null);
    if (refreshList) fetchTechnicians(currentPage);
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") onNavigateToDashboard();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
      case "Active":
        return "status-payment";
      case "INACTIVE":
      case "Inactive":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const formatAssignment = (assignment) => {
    if (!assignment) return "No current assignment";
    const parts = [];
    if (assignment.scheduledDate) parts.push(assignment.scheduledDate);
    if (assignment.scheduledTime) parts.push(assignment.scheduledTime);
    if (assignment.locationType)
      parts.push(
        assignment.locationType === "VISIT_CENTER" ? "On-site" : "Home Visit",
      );
    if (assignment.testTypes?.length)
      parts.push(assignment.testTypes.join(", "));
    return parts.join(" - ") || "No current assignment";
  };

  // Active / Inactive tabs hidden per request — only "All" is shown.
  const orderManagementTabs = ["All"];

  return (
    <LabDashboardLayout
      currentPage="lab_technicianmanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <LabDialogAddEditTechnical
        open={openDialogAddEditTechnical}
        onClose={handleDialogClose}
        type={dialogType}
        technician={selectedTechnician}
      />
      <LabDialogViewTechnical
        open={openDialogViewTechnical}
        onClose={handleDialogClose}
        technician={selectedTechnician}
        onEdit={(tech) => {
          setOpenDialogViewTechnical(false);
          handleEdit(tech);
        }}
      />

      <div className={styles["apt-page-header"]}>
        <h1>Technician Management</h1>
        <button className={styles["apt-register-btn"]} onClick={handleAddNew}>
          <IoAddCircleOutline /> Add New Technician
        </button>
      </div>
      <div className={styles["apt-appointments-section"]}>
        {/* Filters */}
        <div className={styles["apt-filters"]}>
          <div className={styles["apt-filter-group"]}>
            <div className={styles["apt-search-wrapper"]}>
              <Search className={styles["apt-search-icon"]} />
              <input
                type="text"
                placeholder="Search by name, mobile or service code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className={styles["apt-search-input"]}
              />
            </div>
            <button className={styles["apt-reset-btn"]} onClick={resetFilters}>
              Clear Search
            </button>
          </div>
        </div>

        <div className={styles["apt-appointment-tabs"]}>
          {orderManagementTabs.map((tabItem, idx) => (
            <button
              key={idx}
              className={`${styles["apt-tab"]} ${activeTab === tabItem ? styles["active"] : ""}`}
              onClick={() => handleTabChange(tabItem)}
            >
              {tabItem}
            </button>
          ))}
        </div>

        {/* Technicians Table */}
        <div className={styles["apt-appointments-table"]}>
          <table>
            <thead>
              <tr>
                <th>Service Code</th>
                <th>Technician Name</th>
                <th>Gender</th>
                <th>Current Assignment</th>
                <th>Mobile Number</th>
                <th>Availability</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "red",
                    }}
                  >
                    {error}
                  </td>
                </tr>
              ) : technicians.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No technicians found
                  </td>
                </tr>
              ) : (
                technicians.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.serviceCode ||
                        `#${String(item.id).padStart(3, "0")}`}
                    </td>
                    <td>{item.name}</td>
                    <td>{item.gender}</td>
                    <td>
                      <div className={styles["truncateAssignment"]}>
                        {formatAssignment(item.currentAssignment)}
                      </div>
                    </td>
                    <td>{item.mobileNumber}</td>
                    <td>
                      <span
                        className={`${styles["db-status"]} ${styles[getStatusColor(item.status)]}`}
                      >
                        {item.status === "ACTIVE" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          className={styles["apt-action-btn"]}
                          title="View"
                          onClick={() => handleView(item)}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 5C5 5 2 12 2 12s3 7 10 7 10-7 10-7-3-7-10-7z"
                              stroke="#155DFC"
                              strokeWidth="1.5"
                            />
                            <circle
                              cx="12"
                              cy="12"
                              r="3"
                              stroke="#155DFC"
                              strokeWidth="1.5"
                            />
                          </svg>
                        </button>
                        <button
                          className={styles["apt-action-btn"]}
                          title="Edit"
                          onClick={() => handleEdit(item)}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                              stroke="#155DFC"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                              stroke="#155DFC"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        {item.status === "ACTIVE" && (
                          <button
                            className={styles["apt-action-btn"]}
                            title="Delete"
                            onClick={() => handleDelete(item)}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"
                                stroke="#EF4444"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles["apt-pagination"]}>
          <div className={styles["apt-pagination-info"]}>
            Showing{" "}
            {technicians.length === 0
              ? 0
              : (currentPage - 1) * itemsPerPage + 1}{" "}
            to {(currentPage - 1) * itemsPerPage + technicians.length} of{" "}
            {pagination?.total || 0}
          </div>
          <div className={styles["apt-pagination-controls"]}>
            <button
              className={`${styles["apt-pagination-btn"]} ${currentPage === 1 ? styles["disabled"] : ""}`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {t("dashboard.pagination.previous")}
            </button>
            <div className={styles["apt-page-numbers"]}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`${styles["apt-page-btn"]} ${currentPage === page ? styles["active"] : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ),
              )}
            </div>
            <button
              className={`${styles["apt-pagination-btn"]} ${styles["apt-next-btn"]} ${currentPage === totalPages ? styles["disabled"] : ""}`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t("dashboard.pagination.next")}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Technician Confirmation Modal — same style as the Delete Test modal */}
      {technicianToDelete && (
        <div className={dialogStyles["labDialog_wrapper"]}>
          <div
            className={dialogStyles["dialog-overlay"]}
            style={{ alignItems: "center" }}
          >
            <div
              className={dialogStyles["dialog-box"]}
              style={{ width: "460px" }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-technician-title"
            >
              <button
                className={dialogStyles["dialog-close"]}
                onClick={closeDeleteModal}
                disabled={deletingTechnician}
                aria-label="Cancel"
              >
                ×
              </button>
              <h2
                id="delete-technician-title"
                className={dialogStyles["dialog-title"]}
              >
                Delete Technician
              </h2>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: "14px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                Are you sure you want to delete this technician?
                {technicianToDelete.name ? (
                  <>
                    <br />
                    <strong style={{ color: "#111827" }}>
                      {technicianToDelete.name}
                    </strong>
                  </>
                ) : null}
              </p>
              <div
                className={`${dialogStyles["dialog-footer"]} ${dialogStyles["dialog-footer-btn-2"]}`}
              >
                <button
                  className={dialogStyles["btn-outlined-1"]}
                  onClick={closeDeleteModal}
                  disabled={deletingTechnician}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                  }}
                >
                  Cancel
                </button>
                <button
                  className={dialogStyles["btn-1"]}
                  onClick={confirmDeleteTechnician}
                  disabled={deletingTechnician}
                  style={{
                    opacity: deletingTechnician ? 0.6 : 1,
                    justifyContent: "center",
                    border: "1px solid transparent",
                    boxSizing: "border-box",
                  }}
                >
                  <Trash2 size={16} />{" "}
                  {deletingTechnician ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LabDashboardLayout>
  );
};

export default TechnicianManagement;
